import React, {useMemo} from 'react';

import {Avatar} from 'src/components/avatar/avatar';
import {UserName} from 'src/components/user-name/user-name';
import {useUserRoles} from 'src/hooks/useUserRoles';
import {EditComment} from 'src/modules/comments/components/edit-comment/edit-comment';
import {getCommentRoleLabel} from 'src/modules/comments/utils/getCommentRoleLabel';
import {sanitizeComment} from 'src/modules/comments/utils/sanitizeComment';
import {getUsersInfo} from 'src/modules/users-info/services';
import {ChangeRequest} from 'src/openapi/models/ChangeRequest';
import {ChangeRequestComment} from 'src/openapi/models/ChangeRequestComment';
import {useAppSelector} from 'src/store';
import {hasEditRole} from 'src/utils/rolePermissions';

import {Group, Item} from '@pb/kit-component-group';
import {Pill} from '@pb/kit-component-strip-of-pills';

import {commentsBlockItem} from '../../comments.module.styl';

type Props = {
    comment: ChangeRequestComment,
    changeRequest: ChangeRequest | undefined,
    userEmail: string,
    onEditComment: (comment: ChangeRequestComment) => void,
    editedCommentId: number | null,
};

export const Comment = (props: Props) => {
    const {
        comment,
        changeRequest,
        userEmail,
        onEditComment,
        editedCommentId,
    } = props;

    const userRoles = useUserRoles();
    const users = useAppSelector(getUsersInfo);
    const commentRoleLabel = useMemo(() => getCommentRoleLabel(comment.authorEmail, changeRequest),
        [comment.authorEmail, changeRequest]);

    return (
        <Group
            className={commentsBlockItem}
            direction="v"
            gap="xs">
            <Item
                className="pl-s pr-s pt-xs"
                style={{width: '100%'}}>
                <Group alignItems="center">
                    <Avatar user={users[comment.authorEmail]}/>
                    <Group alignItems="center" gap="xs">
                        <strong>
                            <UserName email={comment.authorEmail}/>
                        </strong>
                        {commentRoleLabel ? (
                            <Item className="pt-s">
                                <Pill
                                    name={commentRoleLabel}
                                    variant={commentRoleLabel === 'Author' ? 'DEFAULT' : 'ADDITION'}
                                />
                            </Item>
                        ) : null}
                    </Group>
                    <Item>|</Item>
                    <Item>{comment.creationDate}</Item>
                    {(userEmail === comment.authorEmail &&
                        hasEditRole(userRoles)) && (
                        <EditComment
                            editedCommentId={editedCommentId}
                            currentComment={comment}
                            onEditComment={onEditComment}
                        />
                    )}
                </Group>
            </Item>
            <Item
                className="pl-sm pr-sm pb-s"
                dangerouslySetInnerHTML={{
                    __html: sanitizeComment(comment.comment),
                }}
            />
        </Group>
    );
};
